import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash';

import {
  ACTION_NAMES,
  DATE_FORMATS,
  DB_REPORTS_STATUS_MAPPING,
  SLICES
} from 'src/constants';
import { createResetAction, formatDate, sortSearchedData } from 'src/utils';
import { RootState, StoreMethods } from 'src/state/store';
import { PaginationParams } from 'src/state/user/interfaces';
import {
  BackendPaginationMeta,
  GetReportsByFilterV2PayloadType,
  GetReportsDataV2Type
} from 'src/state/reportV2/interfaces';
import { ReportDataType } from 'src/state/report/interfaces';
import { getTcNotesStatus } from 'src/state/report/tcNotesStatus';
import { getReportsByFiltersV2 } from 'src/state/services/reportV2/api';
import { getReportWritebackAnalysis } from 'src/state/services/report/api';

interface ReportV2State {
  loading: boolean;
  error: null | string;
  searchedReports: ReportDataType[];
  searchedReportsStats: {
    total: number;
    submitted: number;
    complete: number;
    paused: number;
    start: number;
    totalReportCost: number;
  };
  searchedReportsWritebacksStats: {
    total: number;
    success: number;
    pending: number;
    failed: number;
  };
  offset: number;
  limit: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  selectedReport: ReportDataType | null;
  pagination: BackendPaginationMeta;
}

const defaultPagination: BackendPaginationMeta = {
  offset: 0,
  limit: 10,
  totalItems: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false
};

const initialState: ReportV2State = {
  loading: false,
  error: null,
  searchedReports: [],
  searchedReportsStats: {
    total: 0,
    submitted: 0,
    complete: 0,
    paused: 0,
    start: 0,
    totalReportCost: 0
  },
  searchedReportsWritebacksStats: {
    total: 0,
    success: 0,
    pending: 0,
    failed: 0
  },
  offset: 0,
  limit: 10,
  sortBy: '',
  sortDirection: 'asc',
  selectedReport: null,
  pagination: defaultPagination
};

const getTotalCostFromTokens = (
  tokens?: Record<string, string | number | null | undefined>
) => {
  const totalCostInCents = Object.values(tokens || {}).reduce<number>(
    (sum, raw) => {
      const value =
        typeof raw === 'number'
          ? raw
          : typeof raw === 'string'
          ? parseFloat(raw)
          : 0;
      return sum + Math.round(value * 100);
    },
    0
  );

  return totalCostInCents === 0
    ? '$0.00'
    : `$${(totalCostInCents / 100).toFixed(2)}`;
};

const reportV2Slice = createSlice({
  name: 'reportV2',
  initialState,
  reducers: {
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },
    setPagination: (state, action: PayloadAction<PaginationParams>) => {
      state.limit = action.payload.limit;
      state.offset = action.payload.offset;
      state.pagination = {
        ...state.pagination,
        offset: action.payload.offset,
        limit: action.payload.limit
      };
    },
    setSearchedReports: (state, action: PayloadAction<string>) => {
      state.searchedReports = sortSearchedData(
        state.searchedReports,
        action.payload,
        state.sortDirection
      );
    },
    resetSearchedReports: (state) => {
      state.searchedReports = [];
    },
    setSearchedReportsStats: (
      state,
      action: PayloadAction<ReportV2State['searchedReportsStats']>
    ) => {
      state.searchedReportsStats = action.payload;
    },
    setSearchedReportsWritebackStats: (
      state,
      action: PayloadAction<ReportV2State['searchedReportsWritebacksStats']>
    ) => {
      state.searchedReportsWritebacksStats = action.payload;
    },
    setSelectedReport: (
      state,
      action: PayloadAction<ReportDataType | null>
    ) => {
      state.selectedReport = action.payload;
    },
    updateReportTmfStatus: (
      state,
      action: PayloadAction<{ reportId: string; tmfReport: boolean }>
    ) => {
      const { reportId, tmfReport } = action.payload;
      state.searchedReports = state.searchedReports.map((report) => {
        const appointmentId = String(report.appointmentId ?? '');
        const id = String(report.id ?? '');
        const matches = appointmentId === reportId || id === reportId;
        if (!matches) {
          return report;
        }
        return { ...report, tmfReport };
      });
    },
    resetReport: createResetAction(initialState)
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReportsAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchReportsAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.searchedReports = action.payload.data;
      state.searchedReportsStats = action.payload.stats;
      state.searchedReportsWritebacksStats = action.payload.writebackStats;
      state.pagination = action.payload.pagination;
      state.limit = action.payload.pagination.limit;
      state.offset = action.payload.pagination.offset;
    });
    builder.addCase(fetchReportsAsync.rejected, (state, action) => {
      if (action.meta?.aborted) {
        return;
      }
      state.loading = false;
      state.error = (action.payload as string) || null;
      state.searchedReports = [];
      state.searchedReportsStats = {
        total: 0,
        submitted: 0,
        complete: 0,
        paused: 0,
        start: 0,
        totalReportCost: 0
      };
      state.searchedReportsWritebacksStats = {
        total: 0,
        success: 0,
        pending: 0,
        failed: 0
      };
      state.pagination = defaultPagination;
      state.offset = defaultPagination.offset;
      state.limit = defaultPagination.limit;
    });
    builder.addCase(
      getReportWritebackFailureAnalysisAsync.fulfilled,
      (state, action) => {
        const reportWritebackFailureAnalysis = _.get(action, 'payload', {});
        state.selectedReport = {
          ...state.selectedReport,
          writebackAnalysis: {
            ...state.selectedReport?.writebackAnalysis,
            ...reportWritebackFailureAnalysis
          }
        };
      }
    );
  }
});

export const fetchReportsAsync = createAsyncThunk<
  GetReportsDataV2Type,
  GetReportsByFilterV2PayloadType,
  StoreMethods
>(
  `${SLICES.REPORT}/${ACTION_NAMES.GET_REPORTS_BY_FILTERS}_V2`,
  async (payload, { dispatch, rejectWithValue, getState, signal }) => {
    try {
      const state = getState() as RootState;
      const rawOffset = state.reportV2.offset;
      const rawLimit = state.reportV2.limit;
      const offsetFromState =
        Number.isFinite(rawOffset) && rawOffset >= 0
          ? Math.floor(rawOffset)
          : 0;
      const limitFromState =
        Number.isFinite(rawLimit) && rawLimit > 0
          ? Math.min(100, Math.floor(rawLimit))
          : 10;
      const sortBy = state.reportV2.sortBy;
      const sortOrder = state.reportV2.sortDirection.toUpperCase() as
        | 'ASC'
        | 'DESC';

      const reportRes = await getReportsByFiltersV2(
        payload,
        {
          offset: offsetFromState,
          limit: limitFromState,
          ...(sortBy ? { sortBy } : {}),
          ...(sortBy ? { sortOrder } : {})
        },
        { dispatch, signal }
      );

      const reportData = _.get(reportRes, 'data.data', []);
      const rawStats = _.get(reportRes, 'data.stats', {});
      const searchedReportsStats = {
        total: rawStats.total ?? 0,
        submitted: rawStats.submitted ?? 0,
        complete: rawStats.complete ?? 0,
        paused: rawStats.paused ?? 0,
        start: rawStats.start ?? 0,
        totalReportCost: rawStats.totalReportCost ?? 0
      };
      const searchedReportsWritebackStats = _.get(
        reportRes,
        'data.writebackStats',
        {}
      );
      const paginationRaw = _.get(reportRes, 'pagination', {});

      const isPerioChartRowsOnly =
        payload.filters?.includePeriodChartRowsOnly === true;

      const formatPerioChartDate = (date: unknown): string => {
        if (date === undefined || date === null || date === '') {
          return 'N/A';
        }
        const formatted = formatDate({
          date: date as string,
          format: DATE_FORMATS.MONTH_FIRST
        });
        return formatted === 'Invalid date' ? 'Invalid Date' : formatted;
      };

      const modifiedReports = reportData?.map((report) => {
        const tokens = {
          totalReportCost: report?.tokens?.soapReportTotalPrice ?? null,
          totalAnalysisCost: report?.tokens?.analysisTotalPrice ?? null,
          totalReportSumaryCost:
            report?.tokens?.reportSummaryTotalPrice ?? null,
          totalSpecialistReportCost:
            report?.tokens?.specialistReportTotalPrice ?? null,
          totalAfterCareSummaryReportCost:
            report?.tokens?.afterCareSummaryReportTotalPrice ?? null,
          totalFinancialNotesCost: report?.tokens?.tcNotesTotalPrice ?? null
        };

        return {
          id: report?._id,
          appointmentId: report?.appointmentId,
          patientId: report?.patientId,
          patientEmail: report?.patientEmail,
          reportUrl: report?.reportUrl,
          description: report?.description,
          procedureCodes: report?.procedureCodes,
          procedureKeys: report?.procedureKeys,
          patientName: report?.patientName,
          appointmentTime: report?.appointmentTime,
          appointmentDate: formatDate({
            date: report?.appointmentDate,
            format: DATE_FORMATS.MONTH_FIRST
          }),
          appointmentLength: isNaN(Number(report?.appointmentLength))
            ? report?.appointmentLength
            : Number(report?.appointmentLength),
          operatory: report?.operatory,
          status: DB_REPORTS_STATUS_MAPPING[report?.status],
          ...(isPerioChartRowsOnly
            ? {
                perioCreatedAt: formatPerioChartDate(
                  report?.perioChart?.createdAt
                ),
                perioUpdatedAt: formatPerioChartDate(
                  report?.perioChart?.updatedAt
                )
              }
            : {
                createdAt: formatDate({
                  date: report?.createdAt,
                  format: DATE_FORMATS.MONTH_FIRST
                }),
                updatedAt: formatDate({
                  date: report?.updatedAt,
                  format: DATE_FORMATS.MONTH_FIRST
                })
              }),
          userName: report?.userName,
          writebackStatus: report?.writebackStatus,
          providerName: report?.providerId,
          providerId: report?.providerId,
          writebackIds: report?.writebackIds,
          retryWriteback: Boolean(report?.retryWriteback),
          writebackStatusRequested: Boolean(report?.writebackStatusRequested),
          pausedAt: report?.pausedAt,
          pausedTime: report?.pausedTime,
          stoppedAt: report?.stoppedAt,
          reportProcessingAt: report?.reportProcessingAt,
          initialNotificationSentAt: report?.initialNotificationSentAt,
          reportCompletedAt: report?.reportCompletedAt,
          finalNotificationSentAt: report?.finalNotificationSentAt,
          submittedAt: report?.submittedAt,
          submittedForWritebackAt: report?.submittedForWritebackAt,
          writebackSuccessAt: report?.writebackSuccessAt,
          perioWritebackStatus: report?.perioChart?.status
            ? String(report?.perioChart?.status).toUpperCase()
            : 'NA',
          hasTcNotes: getTcNotesStatus(report),
          totalCost: getTotalCostFromTokens(tokens),
          tokens,
          perioChart: report?.perioChart,
          tmfReport: Boolean(report?.tmfReport)
        };
      });

      const rawOff = paginationRaw.offset;
      const rawLim = paginationRaw.limit;
      const resolvedOffsetRaw =
        typeof rawOff === 'number' && Number.isFinite(rawOff)
          ? rawOff
          : offsetFromState;
      const resolvedLimitRaw =
        typeof rawLim === 'number' && Number.isFinite(rawLim)
          ? rawLim
          : limitFromState;
      const resolvedOffset = Math.max(0, Math.floor(resolvedOffsetRaw));
      const resolvedLimit = Math.min(
        100,
        Math.max(1, Math.floor(resolvedLimitRaw))
      );

      return {
        data: modifiedReports,
        stats: searchedReportsStats,
        writebackStats: searchedReportsWritebackStats,
        pagination: {
          offset: resolvedOffset,
          limit: resolvedLimit,
          totalItems: paginationRaw.totalItems ?? 0,
          totalPages: paginationRaw.totalPages ?? 0,
          hasNextPage: Boolean(paginationRaw.hasNextPage),
          hasPreviousPage: Boolean(paginationRaw.hasPreviousPage)
        }
      };
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
          ? err
          : 'An error occurred while fetching reports.';
      return rejectWithValue(message);
    }
  }
);

export const getReportWritebackFailureAnalysisAsync = createAsyncThunk<
  any,
  any,
  StoreMethods
>(
  `${SLICES.REPORT}/${ACTION_NAMES.GET_REPORT_WRITEBACK_FAILURE_ANALYSIS}_V2`,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await getReportWritebackAnalysis(payload, { dispatch });
      return _.get(res, 'data.writebackAnalysis', {});
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const {
  setSortBy,
  setSortDirection,
  setPagination,
  setSearchedReports,
  resetReport,
  resetSearchedReports,
  setSearchedReportsStats,
  setSearchedReportsWritebackStats,
  setSelectedReport,
  updateReportTmfStatus
} = reportV2Slice.actions;

export const getReports = (state: RootState) =>
  state?.reportV2?.searchedReports;
export const getSearchedReportsStats = (state: RootState) =>
  state?.reportV2?.searchedReportsStats;
export const getSearchedReportsWritebackStats = (state: RootState) =>
  state?.reportV2?.searchedReportsWritebacksStats;
export const getSelectedReport = (state: RootState) =>
  state?.reportV2?.selectedReport;
export const getPaginationMeta = (state: RootState) =>
  state?.reportV2?.pagination;
export const getReportV2Loading = (state: RootState) =>
  state?.reportV2?.loading;
export const getReportV2SortBy = (state: RootState) => state?.reportV2?.sortBy;
export const getReportV2SortDirection = (state: RootState) =>
  state?.reportV2?.sortDirection;
export const getReportV2Limit = (state: RootState) => state?.reportV2?.limit;

export default reportV2Slice.reducer;
